import React, { useState } from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMRadioButton from "src/components/UI/atoms/ATMRadioButton/ATMRadioButton";
import { TeamResourceFormValues } from "src/models/TeamResource.model";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { useGetUserQuery } from "src/services/UserServices";
import { RootState } from "src/redux/store";
import { useSelector } from "react-redux";
import useBranches from "src/hooks/useGetBranches";
import MOLDropzone from "src/components/UI/molecules/MOLDropzone/MOLDropzone";

type Props = {
  formikProps: FormikProps<TeamResourceFormValues>;
  onClose: () => void;
  formType: "ADD" | "UPDATE";
};

const resourceTypeData = [
  {
    label: "Only Me",
    value: "ONLY ME",
  },
  {
    label: "Public",
    value: "PUBLIC",
  },
  {
    label: "Shared With",
    value: "SHARED WITH",
  },
];

const TeamResourceForm = ({ formikProps, onClose, formType }: Props) => {
  const { page, rowsPerPage, searchValue } = useSelector(
    (state: RootState) => state.user
  );
  const [selectedBranch, setSelectedBranch] = useState<string[]>([]);

  const { branches, isDataLoading } = useBranches();

  const { data: userDataList, isLoading: isUserListLoading } = useGetUserQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["firstName", "lastName", "mobile", "email"],
    page: page,
    filterBy: [{ fieldName: "branches._id", value: selectedBranch }],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const handleVisibilityChange = (e: any) => {
    setFieldValue("visibility", e);
  };

  const handleBranchClick = (branchId: string) => {
    if (selectedBranch.includes(branchId)) {
      setSelectedBranch(selectedBranch.filter((id) => id !== branchId));
    } else {
      setSelectedBranch([...selectedBranch, branchId]);
    }
  };

  const isChecked = (userId: string) => {
    return values?.sharedWithUserIds?.includes(userId);
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add Resource" : "Update Resource"}{" "}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-4">
        <div>
          <ATMTextField
            placeholder="Enter Title"
            name="title"
            required
            value={values?.title}
            onChange={(e) => {
              setFieldValue("title", e.target.value);
            }}
            label="Title"
            onBlur={handleBlur}
          />
        </div>

        {/* Resource Type */}
        <div className="mt-4">
          <MOLDropzone
            label="Document"
            fileUrl={values?.fileUrl}
            onFileUpload={(fileUrl) => {
              setFieldValue("fileUrl", fileUrl);
            }}
            required
          />
          {/* <ATMFilePickerWrapper
            name="fileUrl"
            label="Document"
            placeholder="select file"
            selectedFile={values?.fileUrl}
            onSelect={(fileUrl) => {
              setFieldValue("fileUrl", fileUrl);
            }}
            required
          /> */}
        </div>
        <div>
          <ATMRadioButton
            label="Visibility"
            name="visibility"
            value={values?.visibility || ""}
            className="grid-cols-3 mt-1"
            options={resourceTypeData}
            onChange={handleVisibilityChange}
            required
          />
        </div>
      </div>

      {values?.visibility === "SHARED WITH" && (
        <div className="">
          <h1 className="p-2 text-lg font-bold">Users</h1>
          <div className="flex gap-2 overflow-auto">
            {isDataLoading ? (
              <div className="flex gap-4">
                <div className="bg-gray-300 border animate-pulse rounded-xl">
                  <p className="py-5 px-7"></p>
                </div>
                <div className="bg-gray-300 border animate-pulse rounded-xl">
                  <p className="py-5 px-7"></p>
                </div>
                <div className="bg-gray-300 border animate-pulse rounded-xl">
                  <p className="py-5 px-7"></p>
                </div>
                <div className="bg-gray-300 border animate-pulse rounded-xl">
                  <p className="py-5 px-7"></p>
                </div>
              </div>
            ) : (
              (branches as any[])?.map((branch: any) => {
                const isSelected = selectedBranch.includes(branch?._id);
                return (
                  <div
                    key={branch?._id}
                    className={`text-center border border-gray-500 rounded-xl w-fit text-nowrap ${
                      isSelected ? "bg-gray-300" : ""
                    }`}
                    onClick={() => handleBranchClick(branch?._id)}
                  >
                    <p className="px-5 py-1">{branch.branchName}</p>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex ">
            <input className="p-2 font-semibold" type="checkbox" />
            <h1 className="p-4 font-semibold">Select All</h1>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {isUserListLoading ? (
              <div className="flex gap-9">
                <p className="flex gap-3">
                  <span className="bg-gray-300 animate-pulse size-4"></span>
                  <span className="bg-gray-300 w-14 h-7 animate-pulse"></span>
                </p>
                <p className="flex gap-3">
                  <span className="bg-gray-300 animate-pulse size-4"></span>
                  <span className="bg-gray-300 w-14 h-7 animate-pulse"></span>
                </p>
                <p className="flex gap-3">
                  <span className="bg-gray-300 animate-pulse size-4"></span>
                  <span className="bg-gray-300 w-14 h-7 animate-pulse"></span>
                </p>
                <p className="flex gap-3">
                  <span className="bg-gray-300 animate-pulse size-4"></span>
                  <span className="bg-gray-300 w-14 h-7 animate-pulse"></span>
                </p>
                <p className="flex gap-3">
                  <span className="bg-gray-300 animate-pulse size-4"></span>
                  <span className="bg-gray-300 w-14 h-7 animate-pulse"></span>
                </p>
              </div>
            ) : (
              (userDataList as any)?.data?.map((item: any) => {
                return (
                  <div key={item?._id} className="flex items-center gap-2 ">
                    <input
                      type="checkbox"
                      id={item?._id}
                      name={item?._id}
                      checked={isChecked(item?._id)}
                      onChange={() => {
                        const newIds = isChecked(item?._id)
                          ? values?.sharedWithUserIds?.filter(
                              (id: any) => id !== item?._id
                            )
                          : [...values.sharedWithUserIds, item?._id];
                        setFieldValue("sharedWithUserIds", newIds);
                      }}
                    />
                    {item?.firstName}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default TeamResourceForm;
